export const SPORTSNEW_PLACEMENT_ATTEMPT_STATE = Object.freeze({
  IN_FLIGHT: 'IN_FLIGHT',
  UNKNOWN: 'UNKNOWN',
  STALE_UNKNOWN: 'STALE_UNKNOWN',
  CONTINUATION: 'CONTINUATION',
  STALE_CONTINUATION: 'STALE_CONTINUATION',
  CONTINUATION_UNKNOWN: 'CONTINUATION_UNKNOWN',
  STALE_CONTINUATION_UNKNOWN: 'STALE_CONTINUATION_UNKNOWN',
});

export const SPORTSNEW_PLACEMENT_RESPONSE_DECISION = Object.freeze({
  RETIRE: 'RETIRE',
  KEEP_UNKNOWN: 'KEEP_UNKNOWN',
  KEEP_CONTINUATION: 'KEEP_CONTINUATION',
});

export const SPORTSNEW_PLACEMENT_RECEIPT_ISSUE_DECISION = Object.freeze({
  RETIRE_UNADMITTED: 'RETIRE_UNADMITTED',
  RETRY_SAME_ATTEMPT: 'RETRY_SAME_ATTEMPT',
});

const REUSABLE_STATES = new Set(Object.values(SPORTSNEW_PLACEMENT_ATTEMPT_STATE));
const CONTINUATION_STATES = new Set([
  SPORTSNEW_PLACEMENT_ATTEMPT_STATE.CONTINUATION,
  SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_CONTINUATION,
  SPORTSNEW_PLACEMENT_ATTEMPT_STATE.CONTINUATION_UNKNOWN,
  SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_CONTINUATION_UNKNOWN,
]);

const placementAttemptOutcome = response => (
  response?.details?.placementAttemptOutcome
  ?? response?.placementAttemptOutcome
  ?? response?.data?.details?.placementAttemptOutcome
  ?? null
);

export const refreshSportsnewPlacementAttempt = (attempt, now) => {
  if (!attempt || !REUSABLE_STATES.has(attempt.state)) return null;
  if (Number(now) < Number(attempt.expiresAt)) return attempt;
  if ([
    SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_UNKNOWN,
    SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_CONTINUATION,
    SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_CONTINUATION_UNKNOWN,
  ].includes(attempt.state)) return attempt;
  const continuation = attempt.state === SPORTSNEW_PLACEMENT_ATTEMPT_STATE.CONTINUATION;
  const continuationUnknown = attempt.state === SPORTSNEW_PLACEMENT_ATTEMPT_STATE.CONTINUATION_UNKNOWN;
  return Object.freeze({
    ...attempt,
    state: continuation
      ? SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_CONTINUATION
      : (
        continuationUnknown
          ? SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_CONTINUATION_UNKNOWN
          : SPORTSNEW_PLACEMENT_ATTEMPT_STATE.STALE_UNKNOWN
      ),
    updatedAt: Number(now),
  });
};

export const canReuseSportsnewPlacementAttempt = (attempt, { userId, fingerprint, accountGeneration }) => (
  Boolean(attempt)
  && REUSABLE_STATES.has(attempt.state)
  && Number(attempt.userId) === Number(userId)
  && attempt.fingerprint === fingerprint
  && attempt.version === 3
  && Number(attempt.accountGeneration) === Number(accountGeneration)
);

export const classifySportsnewPlacementResponse = (response, { continuation = false } = {}) => {
  if (continuation) return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_CONTINUATION;
  if (response?.networkError === true) {
    return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN;
  }
  const httpStatus = Number(response?.httpStatus);
  if (Number.isFinite(httpStatus) && httpStatus >= 500 && httpStatus <= 599) {
    return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN;
  }
  if (response?.status === true && (!Number.isFinite(httpStatus) || (httpStatus >= 200 && httpStatus < 300))) {
    return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.RETIRE;
  }
  const outcome = placementAttemptOutcome(response);
  if (outcome === 'TERMINAL_REPLAY') {
    return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.RETIRE;
  }
  if (outcome === 'DEFINITE_REJECTION' && httpStatus >= 400 && httpStatus < 500) {
    return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.RETIRE;
  }
  if (['STATUS_UNKNOWN', 'COMMAND_CONFLICT'].includes(outcome)) {
    return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN;
  }
  return SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN;
};

export const classifySportsnewPlacementReceiptIssueFailure = response => {
  if (response?.networkError === true) {
    return SPORTSNEW_PLACEMENT_RECEIPT_ISSUE_DECISION.RETRY_SAME_ATTEMPT;
  }
  const httpStatus = Number(response?.httpStatus);
  if (!Number.isFinite(httpStatus) || httpStatus >= 500) {
    return SPORTSNEW_PLACEMENT_RECEIPT_ISSUE_DECISION.RETRY_SAME_ATTEMPT;
  }
  if ([408, 409, 425].includes(httpStatus)) {
    return SPORTSNEW_PLACEMENT_RECEIPT_ISSUE_DECISION.RETRY_SAME_ATTEMPT;
  }
  return SPORTSNEW_PLACEMENT_RECEIPT_ISSUE_DECISION.RETIRE_UNADMITTED;
};

export const transitionSportsnewPlacementAttempt = (attempt, decision, now, ttlMs) => {
  if (!attempt) return null;
  if (decision === SPORTSNEW_PLACEMENT_RESPONSE_DECISION.RETIRE) return null;
  const continuation = decision === SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_CONTINUATION;
  const continuationUnknown = decision === SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN
    && CONTINUATION_STATES.has(attempt.state);
  return Object.freeze({
    ...attempt,
    state: continuation
      ? SPORTSNEW_PLACEMENT_ATTEMPT_STATE.CONTINUATION
      : (
        continuationUnknown
          ? SPORTSNEW_PLACEMENT_ATTEMPT_STATE.CONTINUATION_UNKNOWN
          : SPORTSNEW_PLACEMENT_ATTEMPT_STATE.UNKNOWN
      ),
    updatedAt: Number(now),
    ...(continuation ? { expiresAt: Number(now) + Number(ttlMs) } : {}),
  });
};

export const isSportsnewPlacementContinuationState = state => CONTINUATION_STATES.has(state);
